import { useMemo, useState } from "react";
import useFormFields from "@/hooks/useFormFields";
import { useForm, Controller, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader } from "@/components/shared/loader";
import FormFields from "@/components/shared/form-fields/form-fields";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Pages } from "@/constants/enums";
import {
  AssignRoleDtoRole,
  CreateUserDtoRole,
  UserResponseDtoRole,
} from "@/types/api";
import type { User, UserRole } from "@/types/user";
import { createUserValidation, updateUserValidation } from "@/validations/user";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { useUser } from "@/features/auth/store";
import { filterRolesAssignableByActor } from "@/lib/role-management-policy";
import { useDepartments } from "@/features/departments/hooks/useDepartmentsQueries";
import { useGovernanceAllowedDepartments } from "@/hooks/useDepartmentScope";
import { useRoles } from "@/features/roles/hooks/useRolesQueries";
import {
  useAssignRole,
  useCreateUser,
  useUpdateUser,
} from "@/features/users/hooks/useUsersMutations";
import ReactSelect from "react-select";
import { CredentialsDialog } from "@/features/users/components/CredentialsDialog";

interface UserFormProps {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  user?: User;
}

type UserFormValues = {
  name: string;
  username?: string;
  password?: string;
  email: string;
  role?: string;
  departmentId?: string;
  subAdminDepartmentIds?: string[];
  isActive?: boolean;
};

function toCreateRole(role: string): CreateUserDtoRole {
  return role as CreateUserDtoRole;
}

function toAssignRole(role: UserRole): AssignRoleDtoRole {
  return role as AssignRoleDtoRole;
}

function splitName(name: string): { firstName: string; lastName?: string } {
  const trimmedName = name.trim();
  const nameParts = trimmedName.split(/\s+/);
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || undefined;
  return { firstName, lastName };
}

export function UserForm({ setOpen, user }: UserFormProps) {
  const sessionUser = useUser();
  const { canAssignRole } = useUserPermissions();
  const { getFormFields } = useFormFields({ slug: Pages.USERS });
  const { data: departmentsData } = useDepartments({ limit: 100 });
  const { data: roles = [] } = useRoles();
  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();
  const assignRoleMutation = useAssignRole();

  const [credentials, setCredentials] = useState<{
    username: string;
    temporaryPassword: string;
    email: string;
  } | null>(null);

  const departments = useGovernanceAllowedDepartments(
    useMemo(() => departmentsData?.items ?? [], [departmentsData?.items]),
  );
  const departmentOptions = useMemo(
    () => departments.map((d) => ({ value: d.id, label: d.nameAr })),
    [departments],
  );
  const roleOptions = useMemo(() => {
    const assignable = canAssignRole
      ? filterRolesAssignableByActor(sessionUser, roles)
      : roles.filter((r) => r.role === UserResponseDtoRole.EDITOR);
    return assignable.map((r) => ({ value: r.role, label: r.name }));
  }, [canAssignRole, roles, sessionUser]);

  const baseFields = getFormFields()
    .filter((field) => field.name !== "role")
    .map((field) => {
      if (field.name === "password") {
        return {
          ...field,
          placeholder: user
            ? "اتركها فارغة للإبقاء على كلمة المرور الحالية"
            : field.placeholder,
          description: user
            ? "أدخل كلمة مرور جديدة أو استخدم «توليد تلقائي»"
            : "اترك الحقل فارغاً لتوليد كلمة مرور تلقائياً عند الإنشاء",
        };
      }
      if (field.name === "username") {
        return {
          ...field,
          description: user
            ? "عدّل اسم المستخدم أو استخدم «توليد من الاسم»"
            : "أدخل اسم مستخدم أو استخدم «توليد من الاسم»، أو اتركه فارغاً للتوليد التلقائي عند الإنشاء",
        };
      }
      return field;
    });

  const formMethods = useForm<UserFormValues>({
    defaultValues: {
      name: user
        ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()
        : "",
      email: user?.email ?? "",
      username: user?.username ?? "",
      password: "",
      role: user?.role ?? UserResponseDtoRole.EDITOR,
      departmentId: user?.departmentId ?? undefined,
      subAdminDepartmentIds: user?.subAdminDepartmentIds ?? [],
      isActive: user?.isActive ?? true,
    },
    mode: "onChange",
    resolver: zodResolver(user ? updateUserValidation : createUserValidation),
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = formMethods;

  const selectedRole = useWatch({ control, name: "role" });
  const showSubAdminDepartments =
    canAssignRole && selectedRole === UserResponseDtoRole.SUB_ADMIN;

  const isPending =
    isSubmitting ||
    createMutation.isPending ||
    updateMutation.isPending ||
    assignRoleMutation.isPending;

  async function onSubmit(data: UserFormValues) {
    try {
      const { firstName, lastName } = splitName(data.name);
      const email = data.email.trim();
      const username = data.username?.trim() ?? "";
      const password = data.password?.trim() ?? "";
      const role = data.role ?? UserResponseDtoRole.EDITOR;
      const departmentId =
        data.departmentId?.trim() || undefined;
      const subAdminDepartmentIds = showSubAdminDepartments
        ? (data.subAdminDepartmentIds ?? [])
        : undefined;

      if (user) {
        const roleChanging = canAssignRole && role !== user.role;
        const becomingSubAdmin =
          roleChanging && role === UserResponseDtoRole.SUB_ADMIN;

        if (becomingSubAdmin) {
          await assignRoleMutation.mutateAsync({
            id: user.id,
            data: { role: toAssignRole(role) },
            silentSuccess: true,
          });
        }

        await updateMutation.mutateAsync({
          id: user.id,
          data: {
            email,
            username,
            firstName,
            lastName,
            departmentId,
            isActive: data.isActive,
            ...(password ? { password } : {}),
            ...(subAdminDepartmentIds !== undefined && { subAdminDepartmentIds }),
          },
          silentSuccess: roleChanging && !becomingSubAdmin,
        });

        if (roleChanging && !becomingSubAdmin) {
          await assignRoleMutation.mutateAsync({
            id: user.id,
            data: { role: toAssignRole(role) },
          });
        }
      } else {
        const result = await createMutation.mutateAsync({
          email,
          firstName,
          lastName,
          role: toCreateRole(data.role ?? UserResponseDtoRole.EDITOR),
          departmentId,
          ...(username ? { username } : {}),
          ...(password ? { password } : {}),
          ...(subAdminDepartmentIds !== undefined && { subAdminDepartmentIds }),
        });

        const { temporaryPassword, username: generatedUsername, email: userEmail } =
          result.data;
        if (temporaryPassword && generatedUsername) {
          setCredentials({
            username: generatedUsername,
            temporaryPassword,
            email: userEmail,
          });
          return;
        }
      }

      setOpen(false);
    } catch {
      /* mutations handle errors via onError */
    }
  }

  const handleCredentialsDone = () => {
    setCredentials(null);
    setOpen(false);
  };

  return (
    <>
      <CredentialsDialog
        open={credentials !== null}
        onOpenChange={(open) => {
          if (!open) handleCredentialsDone();
        }}
        username={credentials?.username ?? ""}
        temporaryPassword={credentials?.temporaryPassword ?? ""}
        email={credentials?.email ?? ""}
        onDone={handleCredentialsDone}
      />
    <Form {...formMethods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={cn("max-h-[calc(100vh-200px)]", "overflow-y-auto")}
      >
        {baseFields.map((field, index) => (
          <div key={index} className="mb-4">
            <FormFields {...field} control={control} errors={errors} />
          </div>
        ))}

        {canAssignRole && (
          <div className="mb-4 space-y-2">
            <Label className="text-sm font-medium">نوع الحساب</Label>
            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value ?? UserResponseDtoRole.EDITOR}
                  onValueChange={field.onChange}
                  disabled={!canAssignRole && !!user}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="اختر نوع الحساب" />
                  </SelectTrigger>
                  <SelectContent>
                    {roleOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        )}

        <div className="mb-4 space-y-2">
          <Label className="text-sm font-medium">الدائرة</Label>
          <Controller
            name="departmentId"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value ?? ""}
                onValueChange={field.onChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="اختر الدائرة" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.nameAr}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        {showSubAdminDepartments && (
          <div className="mb-4 space-y-2">
            <Label className="text-sm font-medium">
              الدوائر المُدارة (للتذاكر والحوكمة)
            </Label>
            <Controller
              name="subAdminDepartmentIds"
              control={control}
              render={({ field }) => {
                const value = field.value ?? [];
                const selected = departmentOptions.filter((opt) =>
                  value.includes(opt.value),
                );
                return (
                  <ReactSelect
                    isMulti
                    isRtl
                    options={departmentOptions}
                    value={selected}
                    onChange={(newValue) =>
                      field.onChange(
                        newValue ? newValue.map((opt) => opt.value) : [],
                      )
                    }
                    placeholder="اختر الدوائر التي يديرها هذا المستخدم"
                    className="text-sm"
                  />
                );
              }}
            />
          </div>
        )}

        {user && (
          <div className="mb-4 flex items-center justify-between rounded-lg border border-gray-200 p-3">
            <Label htmlFor="isActive" className="text-sm font-medium">
              الحساب نشط
            </Label>
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <Switch
                  id="isActive"
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>
        )}

        <Button type="submit" disabled={isPending} className="w-full">
          {user ? "تحديث المستخدم" : "إنشاء مستخدم جديد"}
          {isPending && <Loader className="mr-2" />}
        </Button>
      </form>
    </Form>
    </>
  );
}
